import React from 'react';
import { useCookies } from 'next-client-cookies';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { cleanString } from 'helpers/utils/util';
import { Money } from 'types/entity/product';

interface Props {
  discountedPrice?: Money;
  minPrice?: Money;
  textConfig?: Record<string, any>;
  productName?: string;
  hasVariants?: boolean;
  hasSamePrice?: boolean;
  ffid?: string;
}

const Prices: React.FC<Props> = ({
  ffid,
  minPrice,
  discountedPrice,
  textConfig,
  productName,
  hasVariants,
  hasSamePrice,
}) => {

  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const containerClassName = 'text-center text-14 sm:text-16';
  const startingFfidLabel = 'lbl-' + (ffid ? ffid + '-' : '');
  const minimumPrice = CurrencyHelpers.formatForCurrency(minPrice ?? '', locale);
  const discountPrice = CurrencyHelpers.formatForCurrency(discountedPrice ?? '', locale);

  const renderStartingPriceLabel = (fallbackName: string, withLeadingSpace = false) => (
    <span data-ffid={startingFfidLabel + 'product-starting-' + cleanString(productName ?? fallbackName)}>
      {(hasVariants || !hasSamePrice) && (
        <>
          {withLeadingSpace ? ' ' : null}
          {textConfig?.priceStartingLabel ?? 'Starting at'}
        </>
      )}{' '}
    </span>
  );

  const renderA11yPrice = (value: string, label: string, dataFfid: string, className: string) => (
    <span className={className}>
      <span aria-hidden="true" data-ffid={dataFfid}>
        {' '}
        {value}
      </span>
      <span className="sr-only">{`${label} ${value}`}</span>
    </span>
  );

  if (discountedPrice) {
    return (
      <div className={containerClassName}>
        {renderStartingPriceLabel('-', true)}
        {renderA11yPrice(
          minimumPrice,
          textConfig?.minPriceLabel ?? 'Minimum Price',
          startingFfidLabel + 'product-price-' + cleanString(productName ?? '0'),
          'text-[#5C5C5C] line-through',
        )}
        <br className="sm:hidden" />
        {renderA11yPrice(
          discountPrice,
          textConfig?.discountPriceLabel ?? 'Discount Price',
          startingFfidLabel + 'product-discount-price-' + cleanString(productName ?? '0'),
          'text-[#DB0000]',
        )}
      </div>
    );
  }

  if (minPrice) {
    return (
      <div className={containerClassName}>
        {renderStartingPriceLabel('0')}
        <span data-ffid={startingFfidLabel + 'product-price-' + cleanString(productName ?? '0')}>
          {minimumPrice}
        </span>
      </div>
    )
  }

  return null;
};

export default Prices;
